import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { AppEnv } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import {
	DoneReason,
	LEGACY_METRIC_REVIEW_PATH,
	LegacyMetricPopupType,
	MessageType,
} from 'webapp/enums/metric-mover';
import type { LegacyPopupCandidate } from 'webapp/utils/legacy-metric-deletion';
import {
	getDaysUntilDeletion,
	getLegacyPopupSources,
	getLegacyPopupUserRole,
	getTodayInTimezone,
	isBeforeOrOnDeletionDate,
	isValidLegacyDeletionDate,
	pickLegacyPopupCandidate,
} from 'webapp/utils/legacy-metric-deletion';

import MetricMoverLegacyDeletionPopupComponent from 'webapp/components/metric-mover/legacy-deletion-popup';

const MM_SOURCE = 'databox-metric-mover';
const MM_VERSION = 1;

interface MetricMoverMessage {
	source: string;
	v: number;
	type: MessageType;
	payload?: Record<string, unknown>;
}

export interface MetricMoverCallbacks {
	onDone?: (reason: DoneReason) => void;
	onClose?: () => void;
	onResize?: (height: number) => void;
}

export interface MetricMoverOpenOptions {
	spaceAccessId?: string;
	path?: string; // metric mover page, e.g. LEGACY_METRIC_REVIEW_PATH; its start page when omitted
	params?: Readonly<Record<string, string>>; // extra query params for the metric mover URL
	// Skips the feature-flag gate (isLegacyDeprecationActive) only. open() has no admin check:
	// callers apply it (isMetricMoverAvailable / canAccessMetricMover). The legacy metric
	// deletion review (openLegacyMetricReview) uses it, as its condition is the deletion date.
	force?: boolean;
}

export default class MetricMoverService extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('automated-popup-queue')
	declare automatedPopupQueueService: Service.AutomatedPopupQueue;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	@tracked isOpen = false;
	@tracked iframeUrl: string | null = null;

	get isLegacyDeprecationActive(): boolean {
		if (window.env !== AppEnv.PRODUCTION) return true;
		// When enabled globally, the metric mover is active for every space.
		if (this.requestService.metricMoverEnabled) return true;
		// Otherwise fall back to the per-space allowlist.
		const enabledSpaceIds = this.requestService.metricMoverEnabledSpaceIds;
		return Array.isArray(enabledSpaceIds) && enabledSpaceIds.includes(this.accountService.id);
	}

	get isMetricMoverAvailable(): boolean {
		return this.isLegacyDeprecationActive && this.currentUserService.isAdmin;
	}

	private iframeWindow: Window | null = null;
	private messageHandler: ((event: MessageEvent) => void) | null = null;
	private callbacks: MetricMoverCallbacks = {};

	private get metricMoverBaseUrl(): string {
		return (this.requestService.metricMoverCustomerUrl || '').replace(/\/+$/, '');
	}

	private get metricMoverOrigin(): string {
		try {
			return new URL(this.metricMoverBaseUrl).origin;
		} catch {
			return '';
		}
	}

	// The user's timezone: accounts have none (see timeframe.ts for the same fallback).
	private get today(): string {
		return getTodayInTimezone(this.currentUserService.model?.timezone || 'Etc/UTC');
	}

	// Returns whether the metric mover was opened (false when the feature flag is off without
	// `force`, or when its URL is not configured).
	open(options?: MetricMoverOpenOptions, callbacks?: MetricMoverCallbacks): boolean {
		if (!options?.force && !this.isLegacyDeprecationActive) return false;
		if (this.isOpen) this.close();

		const baseUrl = this.metricMoverBaseUrl;
		const origin = this.metricMoverOrigin;
		if (!baseUrl || !origin) {
			return false;
		}

		const params = new URLSearchParams({ embed: '1' });
		Object.entries(options?.params ?? {}).forEach(([key, value]) => params.set(key, value));
		params.set('embed', '1'); // the iframe handshake needs it, so params can't override it
		if (options?.spaceAccessId) {
			params.set('spaceAccessId', options.spaceAccessId);
		}
		const path = `/${(options?.path ?? '').replace(/^\/+/, '')}`;
		this.iframeUrl = `${baseUrl}${path}?${params.toString()}`;
		this.callbacks = callbacks ?? {};
		this.isOpen = true;

		this.messageHandler = (event: MessageEvent) => {
			if (event.origin !== origin) return;
			const msg = event.data as MetricMoverMessage;
			if (msg?.source !== MM_SOURCE || msg.v !== MM_VERSION) return;

			switch (msg.type) {
				case MessageType.READY:
				case MessageType.REQUEST_TOKENS:
					if (!this.iframeWindow && event.source) {
						this.iframeWindow = event.source as Window;
					}
					this.sendTokens();
					break;
				case MessageType.DONE:
					this.callbacks.onDone?.(
						(msg.payload?.reason as DoneReason) ?? DoneReason.COMPLETED,
					);
					this.close();
					break;
				case MessageType.CLOSE:
					this.callbacks.onClose?.();
					this.close();
					break;
				case MessageType.RESIZE:
					if (typeof msg.payload?.height === 'number') {
						this.callbacks.onResize?.(msg.payload.height);
					}
					break;
			}
		};
		window.addEventListener('message', this.messageHandler);

		this.eventManagerService.on(AppEvent.ACCESS_TOKEN_REFRESHED, this, this.sendTokens);

		return true;
	}

	// One-time popup announcing when migrated legacy metrics get deleted, shown once per
	// account. Driven only by the account's deletion dates, not by the metric mover flags.
	addLegacyDeletionPopupToAutomatedPopupQueue(): void {
		const spaceId = this.accountService.id;
		const popupData = this.getLegacyDeletionPopupData(spaceId);

		if (!popupData) {
			return;
		}

		this.automatedPopupQueueService.queuePopup({
			popup: this.popupService.createModal(MetricMoverLegacyDeletionPopupComponent, {
				popupClass: 'popup-type-metric-mover-legacy-deletion',
				showClose: false, // the component renders its own X, which marks the popup as seen
				closeOnOverlayClick: false,
			}),
			popupData,
			checkAddConditions: () => {
				return !this.userSettingsService.hasSeenLegacyMetricPopup(popupData.type, spaceId);
			},
			checkStartConditions: () => {
				// not on top of the metric mover (e.g. opened by the review link on this load)
				return !this.isOpen && isBeforeOrOnDeletionDate(popupData.deletionDate, this.today);
			},
		});
	}

	// Entry point of the Push API email's "Review metrics" link (route legacy-metric-migration/review).
	// Opens the review page like the popup's button does when the Push API popup's date and source
	// rules hold, and marks that popup seen, as the user is already reviewing. Returns whether
	// the review page was opened.
	openLegacyPushMetricReview(): boolean {
		const type = LegacyMetricPopupType.PUSH_API;

		if (!this.getLegacyDeletionSourceNames(type, this.today).length) {
			return false;
		}

		return this.openLegacyMetricReview(type);
	}

	// Opens metric mover's review page for the current account, for anyone (see `force`), and
	// marks that type's deletion popup seen when it opened. Returns whether it opened.
	openLegacyMetricReview(type: LegacyMetricPopupType): boolean {
		const spaceId = this.accountService.id;
		const isOpened = this.open({
			force: true,
			path: LEGACY_METRIC_REVIEW_PATH,
			params: { spaceId: String(spaceId) },
		});

		if (isOpened) {
			this.userSettingsService.markLegacyMetricPopupSeen(type, spaceId);
		}

		return isOpened;
	}

	registerIframe(contentWindow: Window): void {
		this.iframeWindow = contentWindow;
	}

	close(): void {
		if (this.messageHandler) {
			window.removeEventListener('message', this.messageHandler);
			this.messageHandler = null;
		}
		this.eventManagerService.off(AppEvent.ACCESS_TOKEN_REFRESHED, this);
		this.iframeWindow = null;
		this.iframeUrl = null;
		this.isOpen = false;
		this.callbacks = {};
	}

	// When both popups qualify, only the one with the earlier deletion date is returned;
	// the other one shows on a later page load, once this one has been seen.
	private getLegacyDeletionPopupData(
		spaceId: number,
	): PopupData.MetricMover.LegacyDeletion | null {
		const { isOwner, isAdmin } = this.currentUserService;
		const today = this.today;
		const sourceNamesByType = new Map<LegacyMetricPopupType, string[]>();
		const candidates: LegacyPopupCandidate[] = [];

		[LegacyMetricPopupType.SPREADSHEET_SQL, LegacyMetricPopupType.PUSH_API].forEach((type) => {
			const deletionDate = this.getLegacyDeletionDate(type);

			// cheap checks first, before scanning the sources
			if (!deletionDate || this.userSettingsService.hasSeenLegacyMetricPopup(type, spaceId)) {
				return;
			}

			const sourceNames = this.getLegacyDeletionSourceNames(type, today);

			if (!sourceNames.length) {
				return;
			}

			sourceNamesByType.set(type, sourceNames);
			candidates.push({ type, deletionDate });
		});

		const candidate = pickLegacyPopupCandidate(candidates);

		if (!candidate) {
			return null;
		}

		return {
			type: candidate.type,
			spaceId,
			deletionDate: candidate.deletionDate,
			daysUntilDeletion: getDaysUntilDeletion(candidate.deletionDate, today),
			sourceNames: sourceNamesByType.get(candidate.type) ?? [],
			userRole: getLegacyPopupUserRole(isOwner, isAdmin),
		};
	}

	private getLegacyDeletionDate(type: LegacyMetricPopupType): string | null {
		const deletionDate =
			type === LegacyMetricPopupType.PUSH_API
				? this.accountService.legacyPushMetricDeletionDate
				: this.accountService.legacyMetricDeletionDate;

		return isValidLegacyDeletionDate(deletionDate) ? deletionDate : null;
	}

	// The visible sources of the type, sorted, while its deletion date hasn't passed; else none.
	private getLegacyDeletionSourceNames(type: LegacyMetricPopupType, today: string): string[] {
		const deletionDate = this.getLegacyDeletionDate(type);

		if (!deletionDate || !isBeforeOrOnDeletionDate(deletionDate, today)) {
			return [];
		}

		const { isOwner, isAdmin } = this.currentUserService;

		return getLegacyPopupSources(
			this.mmSourcesService.sourceModels,
			type,
			isOwner || isAdmin,
		).map((source) => source.title);
	}

	private sendTokens(): void {
		if (!this.iframeWindow) return;
		const accessToken = this.currentUserService.accessToken;
		const dataToken = this.currentUserService.userDataToken;
		if (!accessToken || !dataToken) return;

		this.iframeWindow.postMessage(
			{
				source: MM_SOURCE,
				v: MM_VERSION,
				type: MessageType.AUTH_TOKENS,
				payload: { accessToken, dataToken },
			},
			this.metricMoverOrigin,
		);
	}

	override willDestroy(): void {
		super.willDestroy();
		this.close();
	}
}

declare module '@ember/service' {
	interface Registry {
		'metric-mover': MetricMoverService;
	}
}
